/// <reference types="vite/client" />
import axios from 'axios';

const API_BASE_URL =
    import.meta.env.VITE_API_BASE_URL ||
    (import.meta.env.PROD ? 'https://api.hscodify.com/api' : '/api');

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 60000,
    headers: { 'Content-Type': 'application/json' },
});

// 401 interceptor — clear stale token and redirect to login
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Only redirect if not already on auth pages
            const isAuthPage = ['/login', '/register'].includes(window.location.pathname);
            if (!isAuthPage) {
                localStorage.removeItem('hsc_token');
                delete api.defaults.headers.common['Authorization'];
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

export interface ClassificationResult {
    id: string;
    hts_code: string;
    hts_code_formatted: string;
    hts_description: string;
    confidence: number;
    alternatives: Array<{
        hts_code: string;
        description: string;
        confidence: number;
    }>;
    rationale: string;
    duty_rate: number;
}

export interface TaxResult {
    hts_code: string;
    hts_description: string;
    declared_value: number;
    duty_rate: number;
    duty_amount: number;
    vat_rate: number;
    vat_amount: number;
    total_tax: number;
    total_landed_cost: number;
    tariff_notes: string[];
    breakdown: Array<{ label: string; amount: number; rate?: number }>;
}

export interface StatsResult {
    total_classifications: number;
    avg_confidence: number;
    top_hts_codes: Array<{
        hts_code: string;
        hts_code_formatted: string;
        count: number;
        avg_confidence: number;
    }>;
    batch_stats: {
        total_batches: number;
        total_batch_items: number;
        completed: number;
        failed: number;
    };
    feedback_stats: {
        total: number;
        corrections: number;
        correction_rate_pct: number;
    };
    daily_activity: Array<{ day: string; count: number }>;
    confidence_distribution: {
        high: number;
        medium: number;
        low: number;
        failed: number;
    };
    hts_codes_count: number;
    ai_provider: {
        provider: string;
        model: string;
        label: string;
    };
}

export interface BatchJob {
    id: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    total_items: number;
    processed_items: number;
    filename: string;
    created_at: string;
    completed_at?: string;
    error_message?: string;
}

// ── Classification ──────────────────────────────────────────
export const classifyProduct = async (
    description: string,
    sku?: string
): Promise<ClassificationResult> => {
    const res = await api.post('/classify', { description, sku });
    return res.data.data;
};

export const uploadBatchCSV = async (
    file: File
): Promise<{ batch_id: string; total_items: number }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post('/classify/batch', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
};

export const getBatchStatus = async (
    batchId: string
): Promise<{ job: BatchJob; classifications: ClassificationResult[] }> => {
    const res = await api.get(`/classify/batch/${batchId}`);
    return res.data;
};

export const getHistory = async (
    page = 1,
    limit = 20,
    source: 'all' | 'single' | 'batch' = 'all'
): Promise<{
    data: ClassificationResult[];
    pagination: { page: number; limit: number; total: number; total_pages: number };
}> => {
    const res = await api.get('/classify/history', { params: { page, limit, source } });
    return res.data;
};

// ── Tax Calculation ─────────────────────────────────────────
export const calculateTax = async (params: {
    hts_code: string;
    declared_value: number;
    origin_country?: string;
    destination_country?: string;
    classification_id?: string;
}): Promise<TaxResult> => {
    const res = await api.post('/tax/calculate', params);
    return res.data.data;
};

// ── Feedback ────────────────────────────────────────────────
export const submitFeedback = async (params: {
    classification_id: string;
    correct_hts_code: string;
    note?: string;
}): Promise<void> => {
    await api.post('/feedback', params);
};

// ── Dashboard Stats ─────────────────────────────────────────
export const getDashboardStats = async (): Promise<StatsResult> => {
    const res = await api.get('/stats');
    return res.data.data;
};

// ── HTS Search ──────────────────────────────────────────────
export const searchHTS = async (q: string): Promise<Array<{
    code: string;
    code_formatted: string;
    description: string;
    duty_rate: number;
}>> => {
    const res = await api.get('/hts/search', { params: { q } });
    return res.data.results;
};

// ── Tariff Origins ──────────────────────────────────────────────────────────
export interface OriginRate {
    country_code:     string;
    label:            string;
    flag:             string;
    rate_pct:         number;
    fta:              string | null;
    notes:            string | null;
    risk:             'danger' | 'warning' | 'safe';
    saving_vs_cn_pct: number;
    source:           'computed' | 'override';
}

export interface TariffOriginsResult {
    hts_code:           string;
    hts_description:    string;
    mfn_base_rate_pct:  number;
    origins:            OriginRate[];
}

export const getTariffOrigins = async (
    htsCode: string,
    countries?: string[],
): Promise<TariffOriginsResult> => {
    const params: Record<string, string> = { hts: htsCode };
    if (countries?.length) params.countries = countries.join(',');
    const res = await api.get('/tariff/origins', { params });
    return res.data.data;
};

// ── Auth helpers ─────────────────────────────────────────────────────────────
export const forgotPassword = async (email: string): Promise<{ message: string; dev_reset_link?: string }> => {
    const res = await api.post('/auth/forgot-password', { email });
    return res.data;
};

export const resetPassword = async (token: string, password: string): Promise<void> => {
    await api.post('/auth/reset-password', { token, password });
};

export default api;
